import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Sparkles } from 'lucide-react';
import BlogCard from './BlogCard';
import BlogCardSkeleton, { BlogCardSkeletonCompact } from './skeletons/BlogCardSkeleton';
import { fetchBlogs } from '../services/blogService';

const BlogPreviewSection = () => {
  const [previewBlogs, setPreviewBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let isMounted = true;
    let idleId = null;
    let timeoutId = null;

    const loadPreviewBlogs = async () => {
      try {
        const data = await fetchBlogs({ limit: 3, sort: 'latest' });
        if (!isMounted) return;
        setPreviewBlogs(Array.isArray(data?.items) ? data.items : []);
        setError('');
      } catch (fetchError) {
        if (isMounted) {
          setPreviewBlogs([]);
          setError(fetchError.message || 'Unable to load latest blogs right now.');
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (typeof window.requestIdleCallback === 'function') {
      idleId = window.requestIdleCallback(loadPreviewBlogs);
    } else {
      timeoutId = window.setTimeout(loadPreviewBlogs, 200);
    }

    return () => {
      isMounted = false;
      if (idleId !== null) window.cancelIdleCallback(idleId);
      if (timeoutId !== null) window.clearTimeout(timeoutId);
    };
  }, []);

  return (
    <section className="relative overflow-hidden bg-[linear-gradient(180deg,#f8f2ff_0%,#f7effb_50%,#fbf7ff_100%)] px-4 py-16 sm:px-6 lg:px-8 lg:py-20">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(144,82,255,0.16),transparent_30%),radial-gradient(circle_at_bottom_right,rgba(255,144,73,0.14),transparent_26%)]" />
      <div className="relative mx-auto grid max-w-7xl gap-10 lg:grid-cols-[0.85fr_1.65fr] lg:items-start">
        <div className="max-w-md pt-2">
          <div className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#7f3df1] to-[#ff7f32] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-white shadow-lg">
            <Sparkles className="h-3.5 w-3.5" />
            Latest Articles
          </div>

          <h2 className="mt-4 text-2xl font-extrabold leading-tight text-[#1f1730] sm:mt-6 sm:text-5xl">
            Latest Articles &
            <span className="mt-1 block bg-gradient-to-r from-[#7f3df1] via-[#c54da9] to-[#ff7f32] bg-clip-text text-transparent">
              Student Stories
            </span>
          </h2>

          <p className="mt-3 max-w-sm text-sm leading-7 text-[#5d536b] sm:mt-6 sm:text-base sm:leading-8">
            Get inspired with real experiences, expert guides, and career tips written for you.
          </p>

          <Link
            to="/blogs"
            className="mt-5 inline-flex items-center gap-1.5 rounded-xl bg-gradient-to-r from-[#7f3df1] via-[#c64ba4] to-[#ff7f32] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_10px_28px_rgba(180,85,175,0.25)] transition-transform duration-300 hover:scale-[1.02] sm:mt-8 sm:gap-2 sm:rounded-2xl sm:px-6 sm:py-4 sm:text-base"
          >
            View All Blogs
            <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5" />
          </Link>
        </div>

        <div className="flex flex-col gap-3 sm:grid sm:grid-cols-3 sm:items-stretch sm:gap-6">
          {loading ? (
            <>
              {[1, 2, 3].map((n) => (
                <React.Fragment key={n}>
                  <div className="sm:hidden"><BlogCardSkeletonCompact /></div>
                  <div className="hidden sm:block"><BlogCardSkeleton /></div>
                </React.Fragment>
              ))}
            </>
          ) : null}

          {!loading && error ? (
            <div className="sm:col-span-3 rounded-[26px] border border-rose-100 bg-rose-50 px-6 py-12 text-center text-sm font-medium text-rose-700 shadow-sm">
              {error}
            </div>
          ) : null}

          {!loading && !error && previewBlogs.length === 0 ? (
            <div className="sm:col-span-3 rounded-[26px] border border-white/70 bg-white/80 px-6 py-12 text-center text-sm font-medium text-[#625470] shadow-sm backdrop-blur">
              Blogs will appear here once the first published posts are available.
            </div>
          ) : null}

          {!loading && !error
            ? previewBlogs.map((blog) => (
                <React.Fragment key={blog.id}>
                  <div className="sm:hidden"><BlogCard blog={blog} compact /></div>
                  <div className="hidden h-full sm:block"><BlogCard blog={blog} /></div>
                </React.Fragment>
              ))
            : null}
        </div>
      </div>
    </section>
  );
};

export default BlogPreviewSection;
